import { Box } from '@mui/material';
import { Controller } from 'react-hook-form';
import { Dayjs } from 'dayjs';
import { InputSelect } from 'app/components/elements/InputSelect';
import { DatePicker } from 'app/components/elements/DatePicker';
import type { CriticalityLevel } from 'utils/issueCriticality';
import { toCriticalitySelectOptions } from 'utils/issueCriticality';

type UserOptionLike = { value: string | number; label: string };

export type IssueAssignmentFormValues = {
  assignedToUserId?: string;
  dueDate?: Dayjs | null;
  status?: string;
  criticalityKey?: string;
};

const DEFAULT_STATUS_OPTIONS = [
  { label: 'Open', value: 'ACTIVE' },
  { label: 'Resolved', value: 'RESOLVED' },
  { label: 'Archived', value: 'ARCHIVED' }
];

export function IssueAssignmentFields({
  control,
  isEditing,
  users,
  criticalityLevels,
  statusOptions = DEFAULT_STATUS_OPTIONS,
  onStatusChange
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: any;
  isEditing: boolean;
  users: UserOptionLike[];
  criticalityLevels: CriticalityLevel[];
  statusOptions?: { label: string; value: string }[];
  onStatusChange?: (status: string) => void;
}) {
  const criticalityOptions = toCriticalitySelectOptions(criticalityLevels, {
    label: '— None —',
    value: ''
  });

  return (
    <Box
      display="grid"
      width="100%"
      gap={2}
      sx={{
        gridTemplateColumns: {
          xs: 'minmax(0, 1fr)',
          sm: 'repeat(2, minmax(0, 1fr))'
        }
      }}>
      <Box sx={{ minWidth: 0, width: '100%' }}>
        <Controller
          control={control}
          name="assignedToUserId"
          render={({ field: { onChange, value } }) => (
            <InputSelect
              value={value || ''}
              title="Assigned To"
              placeholder="Assigned To"
              onChange={(e) => onChange(String(e.target.value))}
              disabled={!isEditing}
              sx={{ padding: 0, minHeight: '46px', height: '46px', width: '100%' }}
              options={users.map((u) => ({ label: u.label, value: String(u.value) }))}
            />
          )}
        />
      </Box>
      <Box sx={{ minWidth: 0, width: '100%' }}>
        <Controller
          control={control}
          name="status"
          render={({ field: { onChange, value } }) => (
            <InputSelect
              value={value || 'ACTIVE'}
              placeholder="Status"
              title="Status"
              onChange={(e) => {
                const next = String(e.target.value);
                onChange(next);
                onStatusChange?.(next);
              }}
              disabled={!isEditing}
              sx={{ padding: 0, minHeight: '46px', height: '46px', width: '100%' }}
              options={statusOptions}
            />
          )}
        />
      </Box>
      <Box sx={{ minWidth: 0, width: '100%' }}>
        <Controller
          control={control}
          name="criticalityKey"
          render={({ field: { onChange, value } }) => (
            <InputSelect
              value={value || ''}
              placeholder="Criticality"
              title="Criticality"
              onChange={(e) => onChange(String(e.target.value))}
              disabled={!isEditing}
              sx={{ padding: 0, minHeight: '46px', height: '46px', width: '100%' }}
              options={criticalityOptions}
            />
          )}
        />
      </Box>
      <Box sx={{ minWidth: 0, width: '100%' }}>
        <Controller
          control={control}
          name="dueDate"
          render={({ field: { value, onChange } }) => (
            <DatePicker
              title="Due Date"
              value={value || null}
              onChange={(next) => onChange(next)}
              dateStyle={{
                pointerEvents: isEditing ? 'auto' : 'none',
                opacity: isEditing ? 1 : 0.6
              }}
            />
          )}
        />
      </Box>
    </Box>
  );
}
